import { useNavigate } from 'react-router-dom';
import { BigNumber } from 'bignumber.js';
import PropTypes from 'prop-types';

import './StorageListPanel.css';

import { useAuth } from '../../../hook/useAuth';
import { useStorage } from '../../../hook/useStorage';
import { buildClass } from '../../../libs/classes';
import { storageFilters, storageOrders } from '../../../hoc/StorageProvider';
import WorkspacePanel, { WorkspacePanelHead } from '../../../components/WorkspacePanel/WorkspacePanel';
import { SimpleSelectLabeled, simpleSelectTypeCls } from '../../../components/SimpleComponents/SimpleSelect';
import DropDownMenu, { dropDownTemplate } from '../../../components/DropDownMenu/DropDownMenu';
import MenuItem from '../../../components/DropDownMenu/MenuItem/MenuItem';
import Spinner from '../../../components/SimpleComponents/Spinner';
import SimpleButton, { ButtonTypes } from '../../../components/SimpleComponents/SimpleButton';
import { buildRoute as buildCollectionRoute } from '../CollectionsPage.Route';

/**
 * Storage List Component. Allows add, modify, delete storages
 * @component
 */
const StorageListPanel = ({
	storages,
	loading,
	onAddStorageReq,
	onModifyStorageReq,
	onMoveSamplesReq,
	onDeleteStorageReq,
	onExportStorageReq,
}) => {
	const navigate = useNavigate();
	const { authenticated, signin } = useAuth();

	const { activeStorage, storageFilter, setStorageFilter, storageOrder, setStorageOrder } = useStorage();

	const getTotalPrice = () => {
		let total_price = BigNumber(0);
		for (let key in storages) {
			let storage = storages[key];
			if (!storage.hidden) total_price = total_price.plus(storage.total_price_usd);
		}
		return total_price.toString();
	};

	const filterStorages = (id) => {
		switch (storageFilter) {
			case storageFilters.all:
				return 1;
			case storageFilters.visible:
				return storages[id].hidden ? 0 : 1;
			case storageFilters.public:
				return storages[id].public ? 1 : 0;
			default:
				return 1;
		}
	};

	const sortStorages = (id1, id2) => {
		let s1 = storages[id1];
		let s2 = storages[id2];
		switch (storageOrder) {
			case storageOrders.shortname:
				if (s1.shortname > s2.shortname) return 1;
				else if (s1.shortname < s2.shortname) return -1;
				return 0;
			case storageOrders.name:
				if (s1.name > s2.name) return 1;
				else if (s1.name < s2.name) return -1;
				return 0;
			case storageOrders.value:
				return s2.total_price_usd - s1.total_price_usd;
			default:
				return s2.id - s1.id;
		}
	};

	const buildStorageRecordClass = (storage) => {
		return buildClass('storage')
			.add(activeStorage && storage?.id === activeStorage?.id, 'active')
			.add(storage.hidden, 'hidden');
	};

	const handleSelectStorage = (storage) => {
		let storageUrl = buildCollectionRoute(storage);
		navigate(storageUrl);
	};

	return (
		<WorkspacePanel className="storage-list">
			<WorkspacePanelHead title="Storages">
				<SimpleButton
					title="Add new storage"
					type={ButtonTypes.DEFAULT}
					value="Add New"
					onClick={onAddStorageReq}
				/>
			</WorkspacePanelHead>

			<div className="subhead">
				<SimpleSelectLabeled
					className={buildClass('filtering', simpleSelectTypeCls.thin)}
					label="Show:"
					value={storageFilter}
					onChange={handleFilterChange}
				>
					<option value={storageFilters.all}>{storageFilters.all}</option>
					<option value={storageFilters.visible}>{storageFilters.visible}</option>
					<option value={storageFilters.public}>{storageFilters.public}</option>
				</SimpleSelectLabeled>
				<SimpleSelectLabeled
					className={buildClass('sorting', simpleSelectTypeCls.thin)}
					label="Sort:"
					value={storageOrder}
					onChange={handleSortChange}
				>
					<option value={storageOrders.name}>{storageOrders.name}</option>
					<option value={storageOrders.shortname}>{storageOrders.shortname}</option>
					<option value={storageOrders.value}>{storageOrders.value}</option>
				</SimpleSelectLabeled>
				<div className="spacer" />
				<div className="total">
					<span className="label">Total value:&nbsp;</span>
					<span className="usd">$&#8202;</span>
					<span className="value">{getTotalPrice()}</span>
				</div>
			</div>
			<div className="content scrollable">
				{!authenticated ? (
					<div className="signin-proposal">
						<span
							className="link"
							href="#"
							onClick={() => {
								signin();
							}}
						>
							Sign in
						</span>{' '}
						to manage your collection
					</div>
				) : null}
				{(loading && (
					<div className="storages-spinner">
						<Spinner />
					</div>
				)) ||
					(Object.keys(storages).length &&
						Object.keys(storages)
							.filter((id) => filterStorages(id))
							.sort((id1, id2) => sortStorages(id1, id2))
							.map((storageId) => (
								<div
									className={buildStorageRecordClass(storages[storageId])}
									key={storageId}
									onClick={() => {
										handleSelectStorage(storages[storageId]);
									}}
								>
									<div className="shortname">{storages[storageId].shortname}</div>
									<div className="name">{storages[storageId].name}</div>
									{storages[storageId].public > 0 ? (
										<div className="public" title="This storage is visible for everyone" />
									) : null}
									<div className="price">
										<span className="usd">$&#8202;</span>
										{storages[storageId].total_price_usd}
									</div>
									<DropDownMenu className="storage-context" template={dropDownTemplate.small_left}>
										<MenuItem text="Modify" onClick={onModifyStorageReq} />
										<MenuItem text="Move" onClick={onMoveSamplesReq} />
										<MenuItem text="Export" onClick={onExportStorageReq} />
										<MenuItem text="Delete" onClick={onDeleteStorageReq} />
									</DropDownMenu>
								</div>
							))) || <div className="no-storages">Storages haven&apos;t been added yet</div>}
			</div>
		</WorkspacePanel>
	);

	function handleSortChange(event) {
		setStorageOrder(event.target.value);
	}
	function handleFilterChange(event) {
		setStorageFilter(event.target.value);
	}
};

StorageListPanel.propTypes = {
	// Associative array of storage objects
	storages: PropTypes.objectOf(PropTypes.object).isRequired,
	// Loading storages
	loading: PropTypes.bool,
	// Callback function on add storage request
	onAddStorageReq: PropTypes.func.isRequired,
	// Callback function on storage modification request
	onModifyStorageReq: PropTypes.func.isRequired,
	// Callback function on samples move request
	onMoveSamplesReq: PropTypes.func.isRequired,
	// Callback function on delete storage request
	onDeleteStorageReq: PropTypes.func.isRequired,
	// Callback function on storage export request
	onExportStorageReq: PropTypes.func.isRequired,
};

export default StorageListPanel;
